import React, { useEffect, useState } from 'react';
import {
  Autocomplete, Avatar, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, MobileStepper, Paper, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { Check, PersonAdd } from '@mui/icons-material';
import { buddyService, UserPublicProfile } from '../services/buddyService';
import { followService } from '../services/followService';
import { userProfileService } from '../services/userProfileService';
import { preferencesManager } from '../utils/preferencesManager';
import { useDiveSiteCatalog } from '../hooks/useDiveSiteCatalog';
import { useNotification } from './NotificationContext';
import { resolveHomeSiteId } from '../utils/regions';
import DiscoverGroupsOrClubsPanel from './DiscoverGroupsOrClubsPanel';

const STEP_COUNT = 4;

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

interface OnboardingWizardProps {
  open: boolean;
  /** Closes the wizard; `writeFirstPost` is true when the diver chose to introduce themselves. */
  onFinish: (writeFirstPost: boolean) => void;
}

/**
 * Welcome flow for brand-new accounts so the feed isn't empty on first load: pick a home
 * site, follow a few divers (same-site divers first), join a group or club, then say hello.
 * Every step is skippable and nothing here is required.
 */
const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ open, onFinish }) => {
  const { showError } = useNotification();
  const { allSites } = useDiveSiteCatalog();
  const [step, setStep] = useState(0);

  // Default on for new accounts, but a relaunch must not flip a choice the diver already made.
  const [weeklyDigest, setWeeklyDigest] = useState(() => preferencesManager.getPreferences()?.customSettings?.notifWeeklyDigest ?? true);
  const [homeSite, setHomeSite] = useState('');
  const [savingSite, setSavingSite] = useState(false);

  const [suggestions, setSuggestions] = useState<UserPublicProfile[]>([]);
  const [loadingDivers, setLoadingDivers] = useState(false);
  const [followedIds, setFollowedIds] = useState<Set<string>>(new Set());
  const [followingId, setFollowingId] = useState<string | null>(null);

  // Load suggestions when entering the follow step — by then the home site is saved, so it can rank them.
  useEffect(() => {
    if (!open || step !== 1) return;
    let cancelled = false;
    setLoadingDivers(true);
    buddyService.getSuggestions(8)
      .then(results => { if (!cancelled) setSuggestions(results); })
      .catch(() => { if (!cancelled) setSuggestions([]); })
      .finally(() => { if (!cancelled) setLoadingDivers(false); });
    return () => { cancelled = true; };
  }, [open, step]);

  const handleNext = async () => {
    if (step === 0 && homeSite.trim()) {
      setSavingSite(true);
      try {
        await userProfileService.updateOwnProfile({ homeSiteName: homeSite.trim(), homeSiteId: resolveHomeSiteId(homeSite, allSites) });
      } catch {
        showError('Could not save your home site — you can set it later in your profile.');
      } finally {
        setSavingSite(false);
      }
    }
    setStep(s => s + 1);
  };

  const handleFollow = async (diver: UserPublicProfile) => {
    setFollowingId(diver.userId);
    try {
      await followService.follow(diver.userId);
      setFollowedIds(prev => new Set(prev).add(diver.userId));
    } catch {
      showError(`Could not follow ${diver.displayName}`);
    } finally {
      setFollowingId(null);
    }
  };

  const lastStep = step === STEP_COUNT - 1;

  // Saved only when the diver finishes the last step, so skipping the flow never opts anyone in.
  const finish = async (writeFirstPost: boolean) => {
    if (lastStep) {
      try {
        const currentPrefs = preferencesManager.getPreferences();
        await preferencesManager.savePreferences({ customSettings: { ...currentPrefs?.customSettings, notifWeeklyDigest: weeklyDigest } });
      } catch { /* non-critical — they can set it in notification settings */ }
    }
    onFinish(writeFirstPost);
  };

  return (
    <Dialog open={open} fullWidth maxWidth="sm" onClose={() => onFinish(false)}>
      <DialogTitle sx={{ fontWeight: 800 }}>
        {['Where do you dive?', 'Follow some divers', 'Join a group or club', "You're all set!"][step]}
      </DialogTitle>
      <DialogContent>
        {step === 0 && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Pick your home dive site so we can introduce you to divers nearby. You can change it any time in your profile.
            </Typography>
            <Autocomplete
              freeSolo
              options={allSites.map(s => s.name)}
              value={homeSite}
              onInputChange={(_, value) => setHomeSite(value)}
              renderInput={(params) => <TextField {...params} label="Home Dive Site" placeholder="Where do you usually dive?" />}
            />
          </Stack>
        )}

        {step === 1 && (
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Following divers fills your feed with their dives and posts.
              {homeSite.trim() ? ` Divers who share your home site, ${homeSite.trim()}, come first.` : ''}
            </Typography>
            {loadingDivers && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>}
            {!loadingDivers && suggestions.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No divers to suggest just yet — you'll find more on the Discover page.
              </Typography>
            )}
            {suggestions.map(diver => {
              const followed = followedIds.has(diver.userId);
              return (
                <Paper key={diver.userId} variant="outlined" sx={{ p: 1.25, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar src={diver.profilePhotoUrl ?? undefined} sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14, fontWeight: 700 }}>
                    {!diver.profilePhotoUrl && (initials(diver.displayName) || '?')}
                  </Avatar>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>{diver.displayName}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {[diver.homeSiteName, diver.diveCountLabel].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant={followed ? 'outlined' : 'contained'}
                    disabled={followed || followingId === diver.userId}
                    startIcon={followed ? <Check /> : <PersonAdd />}
                    onClick={() => handleFollow(diver)}
                  >
                    {followed ? 'Following' : 'Follow'}
                  </Button>
                </Paper>
              );
            })}
          </Stack>
        )}

        {step === 2 && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Groups and clubs have their own feeds, events, and chat. Search by name and ask to join — requests go to the group's admins.
            </Typography>
            <DiscoverGroupsOrClubsPanel kind="group" />
            <DiscoverGroupsOrClubsPanel kind="club" />
          </Stack>
        )}

        {step === 3 && (
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography variant="body1">
              Welcome aboard! Say hello to the community with your first post — where you dive, what you're into, or what you're hoping to see.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You'll choose who can see it, and it defaults to your dive buddies only.
            </Typography>
            <FormControlLabel
              control={<Switch checked={weeklyDigest} onChange={(e) => setWeeklyDigest(e.target.checked)} />}
              label="Email me a weekly summary of what I missed (only when there's something new)"
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <MobileStepper
          variant="dots"
          steps={STEP_COUNT}
          position="static"
          activeStep={step}
          backButton={null}
          nextButton={null}
          sx={{ background: 'transparent', p: 0 }}
        />
        <Stack direction="row" spacing={1}>
          {!lastStep && <Button onClick={() => (step === 0 ? onFinish(false) : setStep(s => s + 1))}>{step === 0 ? 'Skip all' : 'Skip'}</Button>}
          {!lastStep && (
            <Button variant="contained" onClick={handleNext} disabled={savingSite}>
              {savingSite ? <CircularProgress size={20} /> : 'Next'}
            </Button>
          )}
          {lastStep && <Button onClick={() => finish(false)}>Maybe later</Button>}
          {lastStep && <Button variant="contained" onClick={() => finish(true)}>Write my first post</Button>}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default OnboardingWizard;
